import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, OnDestroy, OnInit, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SafeUrl } from '@angular/platform-browser';
import { interval } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '../../../core/auth/auth.service';
import { PermissionsService, type UserRole } from '../../../core/auth/permissions.service';
import { MessagingService } from '../../../pages/mensaxeria/services/messaging.service';
import { MessagingWebSocketService } from '../../../core/services/messaging-websocket.service';
import { FileDownloadService } from '../../../core/services/file-download.service';
import { CandidateService } from '../../../pages/onboarding/services/candidate.service';
import { PanelSocioService } from '../../../pages/panel-socio/services/panel-socio.service';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';

interface HeaderNavItem {
  label: string;
  route: string;
  roles: UserRole[] | null; // null = visible para todos (incluido anónimo)
  guestOnly?: boolean; // si true, solo visible cuando NO está autenticado
  exact?: boolean; // routerLinkActiveOptions exact
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [TranslatePipe, RouterLink, RouterLinkActive, LanguageSelectorComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly messagingService = inject(MessagingService);
  private readonly wsService = inject(MessagingWebSocketService);
  private readonly fileDownloadService = inject(FileDownloadService);
  private readonly candidateService = inject(CandidateService);
  private readonly panelSocioService = inject(PanelSocioService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly isAuthenticated = this.authService.isAuthenticated;
  readonly userName = this.authService.userName;
  readonly activeRole = this.permissionsService.activeRole;
  readonly unreadBadge = this.wsService.badgeText;

  /** Foto de perfil del usuario (solicitante) o logo de empresa (socio) */
  readonly photoUrl = signal<SafeUrl | null>(null);
  private photoRevoke: (() => void) | null = null;
  readonly showPhoto = computed(() => this.photoUrl() !== null);

  /** Iniciales del usuario para el avatar (hash color consistente) */
  readonly userInitials = computed(() => {
    const name = this.userName();
    if (!name) { return ''; }
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) { return parts[0].slice(0, 2).toUpperCase(); }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  });

  /** Color de hash consistente para el avatar basado en el nombre */
  readonly avatarColor = computed(() => {
    const name = this.userName();
    if (!name) { return '#1F2937'; }
    const colors = [
      '#C91812', '#1F2937', '#4B5563', '#15803D',
      '#1D4ED8', '#A16207', '#7E22CE', '#BE123C'
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  });

  /** Items de navegación filtrados por rol */
  readonly navItems = computed(() => {
    const role = this.activeRole();
    return this.allNavItems.filter(item => {
      if (item.guestOnly && role) { return false; }
      if (item.roles === null) { return true; }
      if (!role) { return false; }
      return item.roles.includes(role);
    });
  });

  private readonly allNavItems: HeaderNavItem[] = [
    // Anónimo
    { label: 'header.nav.inicio', route: '/', roles: null, guestOnly: true, exact: true },
    // Solicitante
    { label: 'header.nav.inicio', route: '/vacantes', roles: ['solicitante'], exact: true },
    { label: 'header.nav.mi_panel', route: '/mi-panel', roles: ['solicitante'], exact: true },
    { label: 'header.nav.empleos', route: '/mis-empleos', roles: ['solicitante'] },
    { label: 'header.nav.mensaxeria', route: '/mensaxeria', roles: ['solicitante'] },
    { label: 'header.nav.empresas', route: '/empresas', roles: ['solicitante'] },
    // Socio (orden Figma: inicio → panel → vacantes → candidatos → mensaxería → empresa)
    { label: 'header.nav.inicio', route: '/vacantes', roles: ['socio'], exact: true },
    { label: 'header.nav.mi_panel', route: '/panel-socio', roles: ['socio'], exact: true },
    { label: 'header.nav.vacantes', route: '/mis-vacantes', roles: ['socio'] },
    { label: 'header.nav.candidatos', route: '/explorar-candidatos', roles: ['socio'] },
    { label: 'header.nav.mensaxeria', route: '/mensaxeria', roles: ['socio'] },
  ];

  isMobileMenuOpen = false;
  private wsInitialized = false;
  private loadedPhotoRef: string | null = null;

  constructor() {
    effect(() => {
      const isAuth = this.isAuthenticated();
      const role = this.activeRole();
      untracked(() => {
        if (isAuth && this.authService.hasValidAccessToken()) {
          this.initAuthenticatedFeatures();
          if (role) {
            this.loadProfilePhoto();
          }
        }
      });
    });

    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.isAuthenticated() && this.authService.hasValidAccessToken()) {
          this.loadProfilePhoto();
        }
      });
  }

  ngOnInit(): void {
    if (this.authService.hasValidAccessToken()) {
      this.initAuthenticatedFeatures();
      this.loadProfilePhoto();
    }
  }

  private initAuthenticatedFeatures(): void {
    if (this.wsInitialized) return;
    this.wsInitialized = true;
    void this.wsService.connect();
    this.messagingService.getUnreadCount()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        this.wsService.setTotalUnreadConversations(res.unreadConversations);
      });

    interval(15000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(() => this.wsService.connectionStatus() !== 'connected'),
        switchMap(() => this.messagingService.getUnreadCount())
      )
      .subscribe(res => {
        this.wsService.setTotalUnreadConversations(res.unreadConversations);
      });
  }

  /** Carga la foto de perfil (solicitante) o el logo de empresa (socio) */
  private loadProfilePhoto(): void {
    const role = this.activeRole();
    if (role === 'solicitante') {
      this.candidateService.getMyProfile()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: profile => {
            const photoRef = profile.photo_url ?? null;
            if (!photoRef) {
              this.loadedPhotoRef = null;
              this.clearPhoto();
              return;
            }
            if (photoRef !== this.loadedPhotoRef || !this.photoUrl()) {
              this.loadedPhotoRef = photoRef;
              this.downloadPhoto(photoRef, false);
            }
          },
          error: () => { /* fallback a iniciales */ }
        });
    } else if (role === 'socio') {
      this.panelSocioService.getMyCompany()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: company => {
            const logoRef = company.logoUrl ?? null;
            if (!logoRef) {
              this.loadedPhotoRef = null;
              this.clearPhoto();
              return;
            }
            if (logoRef !== this.loadedPhotoRef || !this.photoUrl()) {
              this.loadedPhotoRef = logoRef;
              this.downloadPhoto(logoRef, true);
            }
          },
          error: () => { /* fallback a iniciales */ }
        });
    }
  }

  /** Descarga la imagen y la asigna al signal photoUrl */
  private downloadPhoto(fileRef: string, isPublic: boolean): void {
    this.photoRevoke?.();
    this.photoRevoke = null;
    this.photoUrl.set(null);

    const download$ = isPublic
      ? this.fileDownloadService.downloadPublicFile(fileRef)
      : this.fileDownloadService.downloadFile(fileRef);

    download$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: file => {
          this.photoRevoke = file.revoke;
          this.photoUrl.set(file.safeUrl);
        },
        error: () => { /* fallback a iniciales */ }
      });
  }

  /** Libera la URL temporal de la foto */
  private clearPhoto(): void {
    this.photoRevoke?.();
    this.photoRevoke = null;
    this.photoUrl.set(null);
  }

  ngOnDestroy(): void {
    this.clearPhoto();
  }

  isUserMenuOpen = false;

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  toggleUserMenu(): void {
    this.isUserMenuOpen = !this.isUserMenuOpen;
  }

  closeUserMenu(): void {
    this.isUserMenuOpen = false;
  }

  login(): void {
    this.authService.login();
  }

  logout(): void {
    this.authService.logout();
    this.isUserMenuOpen = false;
    this.clearPhoto();
    this.router.navigate(['/']);
  }

  navigateToProfile(): void {
    const role = this.activeRole();
    if (role === 'solicitante') {
      this.router.navigate(['/perfil/mi-perfil']);
    } else if (role === 'socio') {
      this.router.navigate(['/mi-empresa']);
    }
    this.isUserMenuOpen = false;
  }

  trackByLabel(_index: number, item: HeaderNavItem): string {
    return item.label + item.route;
  }
}
